/**
 * useHandoffAlerts Hook
 * Subscribes to real-time handoff alerts via Server-Sent Events
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useTeamMember } from '../useTeamMember';
import { subscribeToHandoffAlerts } from '../../api/handoff';
import { Handoff, HandoffSSEEvent, HandoffEventType } from '../../types/handoff.types';
import { HANDOFF_QUERY_KEYS } from './useHandoffQueue';

interface HandoffAlert {
  id: string;
  type: HandoffEventType;
  handoff: Handoff;
  timestamp: Date;
  read: boolean;
}

interface UseHandoffAlertsOptions {
  enabled?: boolean;
  onNewHandoff?: (handoff: Handoff) => void;
  onClaimed?: (handoff: Handoff) => void;
  onDeclined?: (handoff: Handoff) => void;
  onExpired?: (handoff: Handoff) => void;
  maxAlerts?: number;
  autoMarkRead?: boolean;
}

interface UseHandoffAlertsReturn {
  alerts: HandoffAlert[];
  unreadCount: number;
  isConnected: boolean;
  connectionError: boolean;
  markAsRead: (alertId: string) => void;
  markAllAsRead: () => void;
  clearAlerts: () => void;
  reconnect: () => void;
}

export const useHandoffAlerts = (options: UseHandoffAlertsOptions = {}): UseHandoffAlertsReturn => {
  const {
    enabled = true,
    onNewHandoff,
    onClaimed,
    onDeclined,
    onExpired,
    maxAlerts = 50,
    autoMarkRead = false,
  } = options;

  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { teamMemberId } = useTeamMember();
  const organizationId = organization?.id || '';

  const [alerts, setAlerts] = useState<HandoffAlert[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second

  // Store callbacks in refs to prevent stale closures
  const onNewHandoffRef = useRef(onNewHandoff);
  const onClaimedRef = useRef(onClaimed);
  const onDeclinedRef = useRef(onDeclined);
  const onExpiredRef = useRef(onExpired);

  // Update refs when callbacks change
  useEffect(() => {
    onNewHandoffRef.current = onNewHandoff;
    onClaimedRef.current = onClaimed;
    onDeclinedRef.current = onDeclined;
    onExpiredRef.current = onExpired;
  }, [onNewHandoff, onClaimed, onDeclined, onExpired]);

  // Handle incoming handoff events - use refs for callbacks to prevent stale closures
  const handleHandoffEvent = useCallback(
    (event: HandoffSSEEvent) => {
      // Guard against invalid event data
      if (!event?.handoff?.handoffId) {
        console.warn('[Handoff] Received invalid event:', event);
        return;
      }

      const newAlert: HandoffAlert = {
        id: `${event.type}-${event.handoff.handoffId}-${Date.now()}`,
        type: event.type,
        handoff: event.handoff,
        timestamp: new Date(),
        read: autoMarkRead,
      };

      // Add to alerts list
      setAlerts((prev) => {
        const updated = [newAlert, ...prev].slice(0, maxAlerts);
        return updated;
      });

      // Invalidate handoff queue to refetch
      queryClient.invalidateQueries({
        queryKey: HANDOFF_QUERY_KEYS.queue(organizationId),
      });

      // Trigger appropriate callback using refs (always current)
      switch (event.type) {
        case 'new_handoff':
          onNewHandoffRef.current?.(event.handoff);
          break;
        case 'claimed':
          onClaimedRef.current?.(event.handoff);
          break;
        case 'declined':
          onDeclinedRef.current?.(event.handoff);
          break;
        case 'expired':
          onExpiredRef.current?.(event.handoff);
          break;
      }
    },
    [organizationId, queryClient, maxAlerts, autoMarkRead]
  );

  // Calculate exponential backoff delay
  const getReconnectDelay = useCallback(() => {
    const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptRef.current);
    // Cap at 30 seconds
    return Math.min(delay, 30000);
  }, []);

  // Handle successful connection
  const handleConnected = useCallback(() => {
    setIsConnected(true);
    setConnectionError(false);
    reconnectAttemptRef.current = 0; // Reset reconnect attempts on success
  }, []);

  // Handle SSE connection error with exponential backoff
  const handleError = useCallback(
    (_error: Event) => {
      setIsConnected(false);
      setConnectionError(true);

      // Check if we should attempt to reconnect
      if (reconnectAttemptRef.current >= maxReconnectAttempts) {
        return;
      }

      // Clean up existing timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Calculate delay with exponential backoff
      const delay = getReconnectDelay();
      reconnectAttemptRef.current += 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        setConnectionError(false);
        // The connect function will be called by the useEffect when connectionError changes
      }, delay);
    },
    [getReconnectDelay]
  );

  // Connect to SSE
  const connect = useCallback(() => {
    if (!organizationId || !enabled) return;

    // Clean up existing connection
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    // Subscribe synchronously and assign the unsubscribe ref before any await.
    // subscribeToHandoffAlerts mints the stream token itself (its remi Axios
    // interceptor supplies the Bearer header), so there is no outer getIdToken()
    // promise here that a mid-flight unmount could race — teardown always has an
    // unsubscribe to call, and that unsubscribe cancels the in-flight mint before
    // it can open a ghost EventSource.
    unsubscribeRef.current = subscribeToHandoffAlerts(
      organizationId,
      (event: HandoffSSEEvent) => {
        handleHandoffEvent(event);
      },
      handleError,
      handleConnected,
      teamMemberId || undefined
    );
  }, [organizationId, teamMemberId, enabled, handleHandoffEvent, handleError, handleConnected]);

  // Initialize SSE connection
  useEffect(() => {
    connect();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  // Mark a single alert as read
  const markAsRead = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((alert) => (alert.id === alertId ? { ...alert, read: true } : alert))
    );
  }, []);

  // Mark all alerts as read
  const markAllAsRead = useCallback(() => {
    setAlerts((prev) => prev.map((alert) => ({ ...alert, read: true })));
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Manual reconnect
  const reconnect = useCallback(() => {
    setConnectionError(false);
    connect();
  }, [connect]);

  // Calculate unread count
  const unreadCount = alerts.filter((a) => !a.read).length;

  return {
    alerts,
    unreadCount,
    isConnected,
    connectionError,
    markAsRead,
    markAllAsRead,
    clearAlerts,
    reconnect,
  };
};

export default useHandoffAlerts;
